import * as apicall from "../components/Apicall";

// Bookable resources: a garage bay, a van, an exam room, a court - or a person
// such as a driver who logs in. Every call is scoped to the vendor on the
// server (from the token), so none of these send a vendor id.
//
// Reads resolve to { status, success, message, data } and throw on a non-2xx.
// Writes go through apicall.post, which never throws on a 4xx: it hands back
// the parsed body, so callers check `success` (business-rule failures come back
// as { success: false, message }).

const qs = (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );
  const search = new URLSearchParams(clean).toString();
  return search ? `?${search}` : "";
};

/* ---------------------------------------------------------------- *
 *  Resource types (vendor-defined: Bay, Vehicle, Room, Court ...)
 * ---------------------------------------------------------------- */
export const getResourceTypes = (filters = {}) => apicall.get(`resource_types${qs(filters)}`);
export const createResourceType = (data) => apicall.post(data, "resource_types");
export const updateResourceType = (id, data) => apicall.post(data, `resource_types/${id}`);
export const deactivateResourceType = (id) => apicall.post({}, `resource_types/${id}/deactivate`);

/* ---------------------------------------------------------------- *
 *  Resources
 * ---------------------------------------------------------------- */
// filters: { type_id, active: 1 }
export const getResources = (filters = {}) => apicall.get(`resources${qs(filters)}`);
export const createResource = (data) => apicall.post(data, "resources");
// data may carry is_active to reactivate.
export const updateResource = (id, data) => apicall.post(data, `resources/${id}`);
export const deactivateResource = (id) => apicall.post({}, `resources/${id}/deactivate`);
export const deleteResource = (id) => apicall.post({}, `resources/${id}/delete`);
// data.data.future_bookings - shown before a resource is deactivated.
export const getResourceUsage = (id) => apicall.get(`resources/${id}/usage`);

/* ---------------------------------------------------------------- *
 *  Crew pool: who may use a resource (e.g. Van 1 -> drivers Ali and Omar)
 * ---------------------------------------------------------------- */
// Each resource in getResources() carries crew: [{ user_id, name, skill_key }].
// Active crew of this vendor to choose from: [{ user_id, name, skill_key }].
export const getResourceCrewOptions = () => apicall.get("resource_crew_options");
// Replaces the pool with exactly these crew user ids ([] clears it).
export const setResourceCrew = (id, userIds) => apicall.post({ user_ids: userIds }, `resources/${id}/crew`);

/* ---------------------------------------------------------------- *
 *  Availability
 * ---------------------------------------------------------------- */
// date is YYYY-MM-DD. data.data is { bookings: [...], blocks: [...] }.
export const getResourceBusy = (id, date) => apicall.get(`resources/${id}/busy${qs({ date })}`);

// Advisory pre-save clash check for the booking form. data:
//   { resource_ids, date: 'YYYY-MM-DD', time: 'HH:MM', duration_min | offering_ids, exclude_group_id? }
// data.data is { conflict, bookings: [...], blocks: [...] }. The server checks
// again when the booking is saved, so this never blocks anything by itself.
export const checkResources = (data) => apicall.post(data, "resources/check");

/* ---------------------------------------------------------------- *
 *  Manual blocks (maintenance / out of service)
 * ---------------------------------------------------------------- */
export const getResourceBlocks = (id, filters = {}) => apicall.get(`resources/${id}/blocks${qs(filters)}`);
// data: { start_at: 'YYYY-MM-DD HH:MM', end_at, reason? }
export const createResourceBlock = (id, data) => apicall.post(data, `resources/${id}/blocks`);
export const deleteResourceBlock = (blockId) => apicall.post({}, `resource_blocks/${blockId}/delete`);

/* ---------------------------------------------------------------- *
 *  Helpers
 * ---------------------------------------------------------------- */
// Pulls the user-facing message out of a write result.
export const resultMessage = (res, fallback = "Something went wrong") =>
  res?.message || res?.error || fallback;

// A booking's stored value is a comma list ("4,9"); the form edits one id for now.
export const parseResourceIds = (value) =>
  String(value ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => /^\d+$/.test(s) && s !== "0");

// One-line description of a clash result for a toast / banner.
export const describeConflict = (result) => {
  const bookings = result?.bookings || [];
  const blocks = result?.blocks || [];
  const parts = [];
  if (bookings.length) {
    parts.push(
      bookings
        .slice(0, 3)
        .map((b) => `${b.display_booking_id || `#${b.group_id}`} (${b.start}-${b.end})`)
        .join(", ") + (bookings.length > 3 ? ` +${bookings.length - 3} more` : "")
    );
  }
  if (blocks.length) {
    parts.push(blocks.map((b) => b.reason || "Blocked").join(", "));
  }
  return parts.join(" · ");
};
